import { useEffect, useState } from 'react'
import { bundledDocuments, getDocuments, type SiteDocument } from './api/documents'
import { bundledProjects, getProjects } from './api/projects'
import { Terminal } from './components/Terminal'
import type { Project } from './content/projects.seed'
import type { Locale } from './content/site'
import { discordPresence, type DiscordPresence } from './lib/discord'

const LOCALE_KEY = 'mk69-locale-v2'

const initialLocale = (): Locale => {
  try {
    const saved = localStorage.getItem(LOCALE_KEY)
    if (saved === 'ru' || saved === 'en') return saved
  } catch {
    // Storage unavailable — fall through to the browser language.
  }
  return navigator.language?.toLowerCase().startsWith('ru') ? 'ru' : 'en'
}

export default function App() {
  const [locale, setLocale] = useState<Locale>(initialLocale)
  const [projects, setProjects] = useState<Project[]>([])
  /* Seeded from the bundle so `~` is never empty, then refreshed from the API. */
  const [documents, setDocuments] = useState<SiteDocument[]>(bundledDocuments)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [discord, setDiscord] = useState<DiscordPresence>({ state: 'loading' })

  useEffect(() => {
    document.documentElement.lang = locale
    try {
      localStorage.setItem(LOCALE_KEY, locale)
    } catch {
      // Storage unavailable — the choice just won't persist.
    }
  }, [locale])

  useEffect(() => {
    const controller = new AbortController()
    getProjects(controller.signal)
      .then(setProjects)
      .catch(async () => {
        if (controller.signal.aborted) return
        // The bundle ships a seed snapshot, so a dead API degrades to slightly
        // stale data instead of an empty portfolio.
        const bundled = await bundledProjects().catch(() => [])
        if (controller.signal.aborted) return
        if (bundled.length) {
          console.warn('Projects API unavailable — showing the bundled seed catalogue')
          setProjects(bundled)
        } else {
          setFailed(true)
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    getDocuments(controller.signal)
      .then((next) => {
        if (next.length) setDocuments(next)
      })
      .catch(() => {
        // The bundled seed is already on screen; a dead API just freezes it.
        if (!controller.signal.aborted) console.warn('Documents API unavailable — using the seed')
      })
    return () => controller.abort()
  }, [])

  useEffect(() => {
    let controller = new AbortController()

    const load = () => {
      controller.abort()
      const next = new AbortController()
      controller = next
      void discordPresence(next.signal).then((presence) => {
        if (next.signal.aborted) return
        // Skip the state update when nothing changed, so a background poll does
        // not re-render the whole scrollback every minute.
        setDiscord((old) => (JSON.stringify(old) === JSON.stringify(presence) ? old : presence))
      })
    }

    // Polling a public API for a hidden tab is pure waste; refresh on return.
    const tick = () => {
      if (document.visibilityState === 'visible') load()
    }

    load()
    const interval = window.setInterval(tick, 60_000)
    document.addEventListener('visibilitychange', tick)
    return () => {
      controller.abort()
      window.clearInterval(interval)
      document.removeEventListener('visibilitychange', tick)
    }
  }, [])

  return (
    <>
      <a className="skip-link" href="#content">
        Skip to terminal
      </a>
      <Terminal
        locale={locale}
        projects={projects}
        documents={documents}
        loading={loading}
        failed={failed}
        discord={discord}
        onLanguage={setLocale}
      />
    </>
  )
}
